import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SignupForm } from '@/features/auth/signup/components/SignupForm';
import { SignupExpiredCard } from '@/features/auth/signup/components/SignupExpiredCard';
import { SignupAlreadyRegisteredCard } from '@/features/auth/signup/components/SignupAlreadyRegisteredCard';
import { SignupLoading } from '@/features/auth/signup/components/SignupLoading';
import { RequiredTermsLinks } from '@/features/auth/public-terms/components/RequiredTermsLinks';
import { useSignupInvitationQuery } from '@/features/auth/signup/hooks/use-signup-invitation';
import { SignupApiError } from '@/features/auth/signup/types/signup.types';
import { ShcLogo } from '@/components/brand/ShcLogo';
import { AuthFooterOrganization } from '@/features/auth/institution/AuthFooterOrganization';
import { useInstitution } from '@/features/auth/institution/useInstitution';

export const AdminSignupPage = () => {
  const { t } = useTranslation('auth');
  const institution = useInstitution();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  // FLAG_004_01 / VERIFICATION_CROSS_CUTTING_FRONTEND_08: the prototype's `?expired=true` easter egg is
  // REMOVED. The expired screen is legitimate ONLY from a missing token or a real server token rejection
  // (SIGNUP_TOKEN_INVALID) — never fabricated from a query param.
  const invitationQuery = useSignupInvitationQuery(token);

  const apiError =
    invitationQuery.error instanceof SignupApiError ? invitationQuery.error : null;

  // PRD 19 §8.3 / SHCT-259 — invitation link for an already-registered account
  // (SIGNUP_ALREADY_COMPLETED) → render a dedicated "Already signed account" card.
  // Registration is unavailable; the user explicitly clicks "Return to login" to leave
  // (no auto-redirect). Any OTHER invitation error (invalid/expired token) → expired banner.
  const pageState = useMemo<'expired' | 'loading' | 'ready' | 'already-registered'>(() => {
    if (!token) return 'expired'; // No token = no valid entry path; render expired-style banner.
    if (invitationQuery.isLoading) return 'loading';
    if (apiError?.code === 'SIGNUP_ALREADY_COMPLETED') return 'already-registered';
    if (apiError) return 'expired'; // SIGNUP_TOKEN_INVALID surfaces as expired.
    if (invitationQuery.data) return 'ready';
    return 'loading';
  }, [token, invitationQuery.isLoading, invitationQuery.data, apiError]);

  return (
    <main className="login-page">
      <button
        type="button"
        className="signup-page__close-btn"
        aria-label={t('signup.actions.close')}
        onClick={() => navigate('/login')}
      >
        <X size={20} />
      </button>

      <div className="login-page__container" style={{ maxWidth: 520 }}>
        <div className="login-page__logo">
          <div className="login-page__logo-icon">
            <ShcLogo />
          </div>
          <h1 className="login-page__logo-text">{t('brand.serviceName', { ns: 'labels' })}</h1>
        </div>
        {/* Prototype signup.html §663-676 — the expired view hides the subtitle
            and the "have an account?" links, showing only logo + card + footer. */}
        {/* PRD 19 §2.2 No.2 — {organization} + invitation type, branching on the invitation's account
            type. Both halves used to be wrong: the organization was a literal naming the superseded one,
            and there was no branch at all, so someone invited as medical staff read a line telling them
            they had been invited as a system administrator. The admin wording is the default, as the
            requirement document specifies, for the moment before the invitation has loaded. */}
        {pageState !== 'expired' && (
          <p className="signup-page__hospital">
            {t(
              invitationQuery.data?.accountType === 'MEDICAL_STAFF'
                ? 'signup.subtitleMedical'
                : 'signup.subtitleAdmin',
              { institution: institution.name ?? '' },
            )}
          </p>
        )}

        {pageState === 'expired' && <SignupExpiredCard />}
        {pageState === 'loading' && <SignupLoading />}
        {pageState === 'already-registered' && <SignupAlreadyRegisteredCard />}
        {pageState === 'ready' && invitationQuery.data && token && (
          <SignupForm token={token} invitation={invitationQuery.data} />
        )}

        {pageState !== 'expired' && (
          <div className="signup-page__links">
            {t('signup.links.haveAccount')}{' '}
            <Link to="/login">{t('signup.links.login')}</Link>
          </div>
        )}
      </div>

      <footer className="login-page__footer">
        {/* Organization details come from GET /admin/institution (V129), not from the label catalog. */}
        <AuthFooterOrganization />

        {/* Dynamic — links only PUBLISHED terms marked `required` (FEATURE_041). */}
        <RequiredTermsLinks
          className="login-page__footer-terms"
          sepClassName="login-page__footer-sep"
        />
      </footer>
    </main>
  );
};

export default AdminSignupPage;
